import React, { useState, useEffect, useCallback } from 'react';
import AdminNav from '../components/AdminNav';
import { getVolunteers, getAvailabilityTokens, saveAvailabilityToken, updateAvailabilityTokenReminder, getMessageTemplates, DEFAULT_MESSAGE_TEMPLATES, updateVolunteer } from '../services/firebase';
import './Admin.css';

const DAYS = ['thuAvailability', 'friAvailability', 'satAvailability', 'sunAvailability'];

function hasNotProvided(vol) {
  return DAYS.every(d => {
    const val = vol[d];
    return !val || val === '';
  });
}

function isSpanish(congregation) {
  return /spanish/i.test(congregation || '');
}

const MS_DOMAINS = ['outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'hotmail.co.uk', 'outlook.co.uk', 'windowslive.com'];
function isMicrosoftEmail(email) {
  if (!email) return false;
  const domain = (email.split('@')[1] || '').toLowerCase();
  return MS_DOMAINS.includes(domain);
}

// Render the full availability email HTML from a template
function renderAvailabilityEmail(template, firstName, link) {
  const inner = (template.emailHtml || '')
    .replace(/\{\{firstName\}\}/g, firstName)
    .replace(/\{\{link\}\}/g, link);
  return `<!DOCTYPE html><html lang="${template.lang || 'en'}"><body style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:24px;color:#1e293b;">
  <div style="background:#1e293b;color:white;padding:20px 28px;border-radius:8px 8px 0 0;">
    <h2 style="margin:0;font-size:20px;">SMPW Masters Event 2026</h2>
    <p style="margin:4px 0 0;opacity:0.75;font-size:14px;">Augusta, Georgia — April 9–12, 2026</p>
  </div>
  <div style="border:1px solid #e2e8f0;border-top:none;padding:28px;border-radius:0 0 8px 8px;">
    ${inner}
    <p style="margin-top:28px;padding-top:16px;border-top:1px solid #e2e8f0;color:#64748b;font-size:12px;">SMPW Scheduling Team – Augusta Masters</p>
  </div>
</body></html>`;
}

function formatLastViewed(date) {
  if (!date) return '';
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatDate(ts) {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : ts?.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const SMS_TEMPLATES = {
  en: (link) =>
`Hi, this is Brother/Sister [your name]. I'm contacting you because we received your information from your congregation service committee informing us of your desire to participate in the SMPW Masters Event in Augusta this year. Our scheduling team will be assigning you to the different shifts but in order to do so, they need to know your availability during that week. Please use the following link to submit your availability to participate on the cart shifts. Note: if you've also signed up for driving/transportation, you'll receive a separate link requesting your availability for that as well.

Link: ${link}`,
  es: (link) =>
`Saludos, soy Hermano/Hermana [su nombre aquí]. Me comunico con usted porque recibimos su información del comité de servicio de su congregación informándonos de su deseo de participar en la predicación pública durante el evento SMPW Masters en Augusta. Nuestro equipo de programación le asignará los diferentes turnos, pero para ello, necesita conocer su disponibilidad durante esa semana. Por favor utilice el siguiente enlace para enviar su disponibilidad para participar en los turnos de carritos. Nota: si también se ha registrado para conducir/transporte, recibirá un enlace separado solicitando su disponibilidad para eso también.

Enlace: ${link}`,
};

const CONTACT_STATUSES = [
  { value: 'called',        label: 'Called' },
  { value: 'left-vm',       label: 'Left VM' },
  { value: 'no-answer',     label: 'No Answer' },
  { value: 'wrong-number',  label: 'Wrong Number' },
  { value: 'manual-email',  label: 'Sent Email (PWI Augusta)' },
];
function statusLabel(s) {
  return CONTACT_STATUSES.find(x => x.value === s)?.label || '';
}

function AvailabilityRequests({ onLogout }) {
  const [volunteers, setVolunteers] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [templateMap, setTemplateMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('none'); // none | all | sent | responded
  const [channel, setChannel] = useState('email');
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [langOverride, setLangOverride] = useState({}); // volId -> 'en' | 'es'
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [copiedMsgId, setCopiedMsgId] = useState(null);

  // Read last-viewed timestamp once on mount; first-ever visit sets baseline and returns null
  const [lastViewedDate] = useState(() => {
    const stored = localStorage.getItem('availabilityLastViewed');
    if (stored) return new Date(stored);
    localStorage.setItem('availabilityLastViewed', new Date().toISOString());
    return null;
  });

  const handleMarkSeen = () => {
    localStorage.setItem('availabilityLastViewed', new Date().toISOString());
    setBannerDismissed(true);
  };

  const [reminding, setReminding] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [expandedContactId, setExpandedContactId] = useState(null);
  const [contactDraft, setContactDraft] = useState({ status: '', notes: '' });
  const [contactSaving, setContactSaving] = useState(false);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [statusSubFilter, setStatusSubFilter] = useState('all');

  const handleSendReminders = async () => {
    setReminding(true);
    setReminderResult(null);
    let successCount = 0, failCount = 0;

    // If volunteers are selected, remind only those selected who have a pending token
    // Otherwise remind all pending
    const pendingVols = volunteers.filter(v => {
      const tok = tokenMap[v.id];
      const isPending = tok && !tok.used;
      return selected.size > 0 ? selected.has(v.id) && isPending : isPending;
    });

    for (const vol of pendingVols) {
      try {
        const tok = tokenMap[vol.id];
        const contactField = channel === 'sms' ? vol.phone : vol.email;
        if (!contactField) { failCount++; continue; }

        const lang = langOverride[vol.id] ?? (isSpanish(vol.congregation) ? 'es' : 'en');
        const template = templateMap[`availability_${lang}`] || templateMap['availability_en'];
        const link = `https://www.augustapwi.com/availability?t=${tok.token}`;

        const body = { volunteerId: vol.id, firstName: vol.firstName, token: tok.token, channel, to: contactField, isReminder: true };

        if (channel === 'sms' && template?.smsBody) {
          body.renderedSmsBody = template.smsBody
            .replace(/\{\{firstName\}\}/g, vol.firstName)
            .replace(/\{\{link\}\}/g, link);
        } else if (channel === 'email' && template?.emailHtml) {
          body.renderedEmailSubject = `Reminder: ${template.emailSubject}`;
          body.renderedEmailHtml = renderAvailabilityEmail(template, vol.firstName, link);
        }

        await fetch('/api/send-availability-request', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        await updateAvailabilityTokenReminder(tok.token);
        successCount++;
      } catch {
        failCount++;
      }
    }

    setReminderResult({ successCount, failCount });
    await loadData();
    setReminding(false);
  };

  const toggleContact = (vol) => {
    if (expandedContactId === vol.id) {
      setExpandedContactId(null);
    } else {
      setExpandedContactId(vol.id);
      setContactDraft({ status: vol.availContactStatus || '', notes: vol.availContactNotes || '' });
    }
  };

  const handleSaveContact = async (volId) => {
    setContactSaving(true);
    await updateVolunteer(volId, {
      availContactStatus: contactDraft.status,
      availContactNotes: contactDraft.notes,
      availContactUpdatedAt: contactDraft.status ? new Date().toISOString() : null,
    });
    await loadData();
    setContactSaving(false);
    setExpandedContactId(null);
  };

  const toggleLang = (volId) => {
    setLangOverride(prev => {
      const auto = isSpanish(volunteers.find(v => v.id === volId)?.congregation) ? 'es' : 'en';
      const current = prev[volId] ?? auto;
      return { ...prev, [volId]: current === 'es' ? 'en' : 'es' };
    });
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    const [vols, toks, tmplDocs] = await Promise.all([
      getVolunteers(),
      getAvailabilityTokens(),
      getMessageTemplates(),
    ]);
    setVolunteers(vols);
    setTokens(toks);
    // Build template map: id -> merged (Firestore doc over default)
    const map = {};
    for (const id of ['availability_en', 'availability_es']) {
      const fromDB = tmplDocs.find(d => d.id === id);
      map[id] = { ...DEFAULT_MESSAGE_TEMPLATES[id], ...(fromDB || {}) };
    }
    setTemplateMap(map);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Build a map: volunteerId -> most recent token doc
  const tokenMap = {};
  tokens.forEach(t => {
    const existing = tokenMap[t.volunteerId];
    if (!existing || (t.sentAt?.seconds || 0) > (existing.sentAt?.seconds || 0)) {
      tokenMap[t.volunteerId] = t;
    }
  });

  // New responses since last visit (localStorage baseline)
  const newResponses = (!bannerDismissed && lastViewedDate)
    ? tokens
        .filter(t => {
          if (!t.used || !t.respondedAt) return false;
          const d = t.respondedAt?.toDate ? t.respondedAt.toDate()
                  : t.respondedAt?.seconds ? new Date(t.respondedAt.seconds * 1000)
                  : new Date(t.respondedAt);
          return d > lastViewedDate;
        })
        .map(t => volunteers.find(v => v.id === t.volunteerId))
        .filter(Boolean)
        .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
    : [];

  const getVolStatus = (vol) => {
    const tok = tokenMap[vol.id];
    if (tok?.used) return 'responded';
    if (tok && !tok.used) return 'sent';
    if (!hasNotProvided(vol)) return 'responded';
    return 'none';
  };

  const getVolDeliveryStatus = (vol) => {
    const tok = tokenMap[vol.id];
    if (tok?.used || !hasNotProvided(vol)) return 'responded';
    if (tok?.reminderSentAt) return 'reminded';
    if (tok && !tok.used) return 'sent';
    return 'none';
  };

  const stats = {
    total: volunteers.length,
    none: volunteers.filter(v => getVolStatus(v) === 'none').length,
    sent: volunteers.filter(v => getVolStatus(v) === 'sent').length,
    responded: volunteers.filter(v => getVolStatus(v) === 'responded').length,
    microsoft: volunteers.filter(v => isMicrosoftEmail(v.email)).length,
    contacted: volunteers.filter(v => v.availContactStatus).length,
  };

  const handleSort = (field) => {
    if (field === sortField) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortDir2 = sortDir === 'asc' ? 1 : -1;
  const byName = (a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '');

  const filtered = volunteers.filter(v => {
    let passMain;
    if (filter === 'all') passMain = true;
    else if (filter === 'microsoft') passMain = isMicrosoftEmail(v.email);
    else if (filter === 'contacted') passMain = !!v.availContactStatus;
    else passMain = getVolStatus(v) === filter;
    if (!passMain) return false;
    if (statusSubFilter !== 'all') return getVolDeliveryStatus(v) === statusSubFilter;
    return true;
  }).sort((a, b) => {
    if (sortField === 'name')         return sortDir2 * byName(a, b);
    if (sortField === 'congregation') return sortDir2 * ((a.congregation || '').localeCompare(b.congregation || '')) || byName(a, b);
    if (sortField === 'status')       return sortDir2 * (getVolStatus(a).localeCompare(getVolStatus(b))) || byName(a, b);
    if (sortField === 'contact')      return sortDir2 * ((a.availContactStatus || '').localeCompare(b.availContactStatus || '')) || byName(a, b);
    return byName(a, b);
  });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(new Set(filtered.map(v => v.id)));
    } else {
      setSelected(new Set());
    }
  };

  const handleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleGetLink = async (vol) => {
    const tok = tokenMap[vol.id];
    let link;
    if (tok && !tok.used) {
      link = `https://www.augustapwi.com/availability?t=${tok.token}`;
    } else {
      setGeneratingId(vol.id);
      const token = crypto.randomUUID();
      await saveAvailabilityToken(vol.id, token, 'manual');
      await loadData();
      link = `https://www.augustapwi.com/availability?t=${token}`;
      setGeneratingId(null);
    }
    await navigator.clipboard.writeText(link);
    setCopiedId(vol.id);
    setTimeout(() => setCopiedId(null), 2500);
  };

  const handleCopyMessage = async (vol) => {
    const tok = tokenMap[vol.id];
    let link;
    if (tok && !tok.used) {
      link = `https://www.augustapwi.com/availability?t=${tok.token}`;
    } else {
      setGeneratingId(vol.id);
      const token = crypto.randomUUID();
      await saveAvailabilityToken(vol.id, token, 'manual');
      await loadData();
      link = `https://www.augustapwi.com/availability?t=${token}`;
      setGeneratingId(null);
    }
    const lang = langOverride[vol.id] ?? (isSpanish(vol.congregation) ? 'es' : 'en');
    const message = SMS_TEMPLATES[lang](link);
    await navigator.clipboard.writeText(message);
    setCopiedMsgId(vol.id);
    setTimeout(() => setCopiedMsgId(null), 2500);
  };

  const handleSend = async () => {
    setSending(true);
    setSendResult(null);
    let successCount = 0, failCount = 0;

    const toSend = volunteers.filter(v => selected.has(v.id));

    for (const vol of toSend) {
      try {
        const token = crypto.randomUUID();
        const contactField = channel === 'sms' ? vol.phone : vol.email;

        if (!contactField) {
          failCount++;
          continue;
        }

        await saveAvailabilityToken(vol.id, token, channel);

        const lang = langOverride[vol.id] ?? (isSpanish(vol.congregation) ? 'es' : 'en');
        const template = templateMap[`availability_${lang}`] || templateMap['availability_en'];
        const link = `https://www.augustapwi.com/availability?t=${token}`;

        const body = { volunteerId: vol.id, firstName: vol.firstName, token, channel, to: contactField };

        if (channel === 'sms' && template?.smsBody) {
          body.renderedSmsBody = template.smsBody
            .replace(/\{\{firstName\}\}/g, vol.firstName)
            .replace(/\{\{link\}\}/g, link);
        } else if (channel === 'email' && template?.emailHtml) {
          body.renderedEmailSubject = template.emailSubject;
          body.renderedEmailHtml = renderAvailabilityEmail(template, vol.firstName, link);
        }

        await fetch('/api/send-availability-request', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        successCount++;
      } catch {
        failCount++;
      }
    }

    setSendResult({ successCount, failCount });
    setSelected(new Set());
    await loadData();
    setSending(false);
  };

  if (loading) {
    return <div className="admin-container"><div className="loading">Loading...</div></div>;
  }

  const allFilteredSelected = filtered.length > 0 && filtered.every(v => selected.has(v.id));

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Availability Requests</h1>
        <p className="page-subtitle">Send availability request links to volunteers who haven't provided their availability yet.</p>
      </header>

      <div className="scheduling-content">
        {/* New Responses Banner */}
        {newResponses.length > 0 && (
          <div className="avail-new-banner">
            <div>
              <strong>✨ {newResponses.length} new response{newResponses.length !== 1 ? 's' : ''}</strong>
              {' '}since your last visit ({formatLastViewed(lastViewedDate)})
            </div>
            <div className="avail-new-names">
              {newResponses.slice(0, 6).map(v => `${v.lastName}, ${v.firstName}`).join(' · ')}
              {newResponses.length > 6 && ` · +${newResponses.length - 6} more`}
            </div>
            <button className="avail-new-dismiss" onClick={handleMarkSeen}>Mark as seen ✓</button>
          </div>
        )}

        {/* Stat Cards */}
        <div className="stats-grid" style={{ marginBottom: '24px' }}>
          <div className="stat-card">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total Volunteers</div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #dc2626' }}>
            <div className="stat-number" style={{ color: '#dc2626' }}>{stats.none}</div>
            <div className="stat-label">Not Yet Provided</div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #ca8a04' }}>
            <div className="stat-number" style={{ color: '#ca8a04' }}>{stats.sent}</div>
            <div className="stat-label">Sent (Pending)</div>
          </div>
          <div className="stat-card info">
            <div className="stat-number">{stats.responded}</div>
            <div className="stat-label">Responded</div>
          </div>
        </div>

        {/* Filter Pills */}
        <div className="filter-pill-row" style={{ marginBottom: '16px' }}>
          {[
            { key: 'none',      label: `Not Yet Provided (${stats.none})` },
            { key: 'all',       label: `All Volunteers (${stats.total})` },
            { key: 'sent',      label: `Sent (${stats.sent})` },
            { key: 'responded', label: `Responded (${stats.responded})` },
            { key: 'microsoft', label: `⚠️ Microsoft Accounts (${stats.microsoft})` },
            { key: 'contacted', label: `📞 Contacted (${stats.contacted})` },
          ].map(f => (
            <button
              key={f.key}
              className={`filter-pill ${filter === f.key ? 'active' : ''}`}
              onClick={() => { setFilter(f.key); setSelected(new Set()); setStatusSubFilter('all'); }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Sub-filter: delivery status */}
        {filter !== 'responded' && (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', color: '#64748b', marginRight: '2px' }}>Delivery status:</span>
            {[
              { key: 'all',       label: 'All' },
              { key: 'none',      label: 'Not Sent' },
              { key: 'sent',      label: 'Sent' },
              { key: 'reminded',  label: 'Reminded' },
              { key: 'responded', label: 'Responded' },
            ].map(f => (
              <button
                key={f.key}
                className={`filter-pill ${statusSubFilter === f.key ? 'active' : ''}`}
                style={{ fontSize: '12px', padding: '3px 10px' }}
                onClick={() => setStatusSubFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Channel Toggle */}
        <div className="avail-channel-toggle">
          <strong>Send via:</strong>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input type="radio" name="channel" value="email" checked={channel === 'email'} onChange={() => setChannel('email')} />
            Email
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input type="radio" name="channel" value="sms" checked={channel === 'sms'} onChange={() => setChannel('sms')} />
            SMS
          </label>
        </div>

        {/* Send Result Banner */}
        {sendResult && (
          <div className={`result-message ${sendResult.failCount === 0 ? 'success' : 'error'}`} style={{ marginBottom: '16px' }}>
            Sent {sendResult.successCount} request{sendResult.successCount !== 1 ? 's' : ''}
            {sendResult.failCount > 0 && ` · ${sendResult.failCount} failed (missing ${channel === 'sms' ? 'phone' : 'email'})`}.
          </div>
        )}

        {/* Action buttons — always visible */}
        {(() => {
          const selectedPendingCount = [...selected].filter(id => {
            const tok = tokenMap[id];
            return tok && !tok.used;
          }).length;
          const reminderCount = selected.size > 0 ? selectedPendingCount : stats.sent;
          return (
            <div style={{ marginTop: '4px', marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                className="btn btn-primary"
                disabled={selected.size === 0 || sending}
                onClick={handleSend}
              >
                {sending ? 'Sending…' : `Send Requests (${selected.size} selected)`}
              </button>
              <button
                className="btn btn-secondary"
                disabled={reminderCount === 0 || reminding || sending}
                onClick={handleSendReminders}
                title={selected.size > 0
                  ? 'Send reminder to selected volunteers who have a pending token'
                  : 'Resend the same link to all volunteers sent a request but who have not yet responded'}
              >
                {reminding ? 'Sending reminders…'
                  : selected.size > 0
                    ? `Send Reminder to selected (${selectedPendingCount} pending)`
                    : `Send Reminder to all pending (${stats.sent})`}
              </button>
            </div>
          );
        })()}
        {reminderResult && (
          <div className={`result-message ${reminderResult.failCount === 0 ? 'success' : 'error'}`} style={{ marginBottom: '16px' }}>
            Reminder sent to {reminderResult.successCount} volunteer{reminderResult.successCount !== 1 ? 's' : ''}
            {reminderResult.failCount > 0 && ` · ${reminderResult.failCount} skipped (missing ${channel === 'sms' ? 'phone' : 'email'})`}.
          </div>
        )}

        {/* Volunteer Table */}
        {filtered.length === 0 ? (
          <div className="no-data">No volunteers in this category.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="avail-vol-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={handleSelectAll}
                      title="Select all"
                    />
                  </th>
                  {[
                    { label: 'Name', field: 'name' },
                    { label: 'Congregation', field: 'congregation' },
                  ].map(col => (
                    <th key={col.field} className="vol-load-sort-th" onClick={() => handleSort(col.field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      {col.label}{' '}{sortField === col.field ? (sortDir === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.3 }}>⇅</span>}
                    </th>
                  ))}
                  <th>{channel === 'sms' ? 'Phone' : 'Email'}</th>
                  <th>Lang</th>
                  <th className="vol-load-sort-th" onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Status{' '}{sortField === 'status' ? (sortDir === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.3 }}>⇅</span>}
                  </th>
                  <th>Last Contact</th>
                  <th className="vol-load-sort-th" onClick={() => handleSort('contact')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Outreach{' '}{sortField === 'contact' ? (sortDir === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.3 }}>⇅</span>}
                  </th>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(vol => {
                  const volStatus = getVolStatus(vol);
                  const contact = channel === 'sms' ? vol.phone : vol.email;
                  const spanish = isSpanish(vol.congregation);
                  return (
                    <React.Fragment key={vol.id}>
                      <tr>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.has(vol.id)}
                            onChange={() => handleSelect(vol.id)}
                          />
                        </td>
                        <td>
                          {vol.lastName}, {vol.firstName}
                          {vol.isDeafHH && <span className="roster-badge badge-deaf" title="Deaf / Hard of Hearing — email/text only" style={{ marginLeft: 5, fontSize: '11px' }}>🦻 D/HH</span>}
                        </td>
                        <td>{vol.congregation || '—'}</td>
                        <td style={{ color: contact ? '#1e293b' : '#94a3b8', fontSize: '13px' }}>
                          {contact || `No ${channel === 'sms' ? 'phone' : 'email'}`}
                          {channel === 'email' && isMicrosoftEmail(vol.email) && (
                            <span title="Microsoft accounts may be blocked by SendGrid. Consider sending manually." style={{ marginLeft: '5px', background: '#fef3c7', color: '#92400e', fontSize: '10px', padding: '1px 5px', borderRadius: '3px', whiteSpace: 'nowrap' }}>⚠️ MS</span>
                          )}
                        </td>
                        <td>
                          {(() => {
                            const auto = spanish ? 'es' : 'en';
                            const lang = langOverride[vol.id] ?? auto;
                            const isOverridden = langOverride[vol.id] && langOverride[vol.id] !== auto;
                            return (
                              <button
                                className={`msg-lang-badge ${lang}`}
                                style={{ fontSize: '11px', cursor: 'pointer', border: isOverridden ? '2px solid #f59e0b' : undefined }}
                                onClick={() => toggleLang(vol.id)}
                                title="Click to toggle language for this volunteer"
                              >
                                {lang.toUpperCase()}
                              </button>
                            );
                          })()}
                        </td>
                        <td>
                          <span className={`avail-status-badge ${volStatus === 'responded' ? 'done' : volStatus === 'sent' ? 'sent' : 'none'}`}>
                            {volStatus === 'responded' ? 'Responded' : volStatus === 'sent' ? 'Sent' : 'Not Provided'}
                          </span>
                        </td>
                        <td className="avail-last-contact-cell">
                          {tokenMap[vol.id]?.sentAt && (
                            <div>Sent: {formatDate(tokenMap[vol.id].sentAt)}</div>
                          )}
                          {tokenMap[vol.id]?.reminderSentAt && (
                            <div className="avail-reminder-date">Reminder: {formatDate(tokenMap[vol.id].reminderSentAt)}</div>
                          )}
                        </td>
                        <td>
                          {vol.availContactStatus ? (
                            <div>
                              <button
                                className={`contact-status-badge ${vol.availContactStatus}`}
                                onClick={() => toggleContact(vol)}
                                title="Edit contact log"
                              >
                                {statusLabel(vol.availContactStatus)}
                              </button>
                              {vol.availContactUpdatedAt && (
                                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                                  {new Date(vol.availContactUpdatedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                                </div>
                              )}
                            </div>
                          ) : (
                            <button
                              className="btn-log-contact"
                              title="Log outreach attempt"
                              onClick={() => toggleContact(vol)}
                            >
                              + Log
                            </button>
                          )}
                        </td>
                        <td>
                          {volStatus !== 'responded' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <button
                                className="btn-get-link"
                                onClick={() => handleGetLink(vol)}
                                disabled={generatingId === vol.id}
                                title="Copy link to clipboard for manual SMS"
                              >
                                {generatingId === vol.id ? 'Generating…'
                                 : copiedId === vol.id   ? '✓ Copied!'
                                 : tokenMap[vol.id] && !tokenMap[vol.id].used ? '📋 Copy Link'
                                 : '🔗 Get Link'}
                              </button>
                              <button
                                className="btn-get-link"
                                onClick={() => handleCopyMessage(vol)}
                                disabled={generatingId === vol.id}
                                title="Copy full SMS message with link"
                                style={{ borderColor: '#8b5cf6', color: '#8b5cf6' }}
                              >
                                {copiedMsgId === vol.id ? '✓ Copied!' : '💬 Copy Msg'}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {expandedContactId === vol.id && (
                        <tr>
                          <td colSpan={9} style={{ padding: 0, background: '#f8fafc' }}>
                            <div className="contact-log-panel">
                              <div className="contact-log-status-row">
                                {CONTACT_STATUSES.map(s => (
                                  <label key={s.value} className="contact-log-radio">
                                    <input
                                      type="radio"
                                      name={`contact-${vol.id}`}
                                      value={s.value}
                                      checked={contactDraft.status === s.value}
                                      onChange={() => setContactDraft(d => ({ ...d, status: s.value }))}
                                    />
                                    {s.label}
                                  </label>
                                ))}
                                {contactDraft.status && (
                                  <button className="contact-log-clear" onClick={() => setContactDraft(d => ({ ...d, status: '' }))}>✕ Clear</button>
                                )}
                              </div>
                              <textarea
                                className="contact-log-notes"
                                placeholder="Notes (optional — e.g. callback time, reason)…"
                                value={contactDraft.notes}
                                onChange={e => setContactDraft(d => ({ ...d, notes: e.target.value }))}
                                rows={2}
                              />
                              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <button className="btn btn-primary" style={{ fontSize: '13px', padding: '4px 14px' }} onClick={() => handleSaveContact(vol.id)} disabled={contactSaving}>
                                  {contactSaving ? 'Saving…' : 'Save'}
                                </button>
                                <button className="btn btn-secondary" style={{ fontSize: '13px', padding: '4px 14px' }} onClick={() => setExpandedContactId(null)}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AvailabilityRequests;
